import { generateBoutiqueLogoHTML } from '../components/boutiques/BoutiqueLogo';

/**
 * ✅ ATELIER : Service d'impression des tickets de commandes atelier.
 * 
 * Indépendant de TicketPrintService (qui gère les ventes).
 * Format 80mm compatible imprimantes thermiques de caisse.
 * Style : alignement gauche/droite (label/valeur) comme les tickets de ventes.
 */

class OrderTicketPrintService {

  // ============================================================
  // ============== IMPRESSION ==================================
  // ============================================================

  static async printOrderTicket(orderData, boutiqueInfo, creatorInfo) {
    try {
      console.log('🖨️ Impression ticket commande:', orderData?.orderNumber);

      const html = this.generateOrderTicketHTML(orderData, boutiqueInfo, creatorInfo);

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      const printWindow = window.open(
        '',
        '_blank',
        isMobile 
          ? 'scrollbars=yes'
          : 'width=400,height=700,scrollbars=yes'
      );

      if (!printWindow) {
        throw new Error('Popup bloqué. Autorisez les popups pour imprimer.');
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      await new Promise((resolve) => {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            resolve();
          }, 500);
        };
        setTimeout(resolve, 1000);
      });

      return {
        success: true,
        message: `Bon de commande ${orderData.orderNumber} envoyé à l'impression`
      };

    } catch (error) {
      console.error('❌ Erreur impression commande:', error);
      return {
        success: false,
        message: error.message || "Erreur lors de l'impression"
      };
    }
  }

  static async previewOrderTicket(orderData, boutiqueInfo, creatorInfo) {
    try {
      console.log('👁️ Aperçu ticket commande:', orderData?.orderNumber);

      const html = this.generateOrderTicketHTML(
        orderData, boutiqueInfo, creatorInfo, true
      );

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      const previewWindow = window.open(
        '',
        '_blank',
        isMobile 
          ? 'scrollbars=yes'
          : 'width=400,height=700,scrollbars=yes'
      );

      if (!previewWindow) {
        throw new Error("Popup bloqué. Autorisez les popups pour afficher l'aperçu.");
      }

      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();

      return { success: true, message: 'Aperçu affiché' };

    } catch (error) {
      console.error('❌ Erreur aperçu commande:', error);
      return {
        success: false,
        message: error.message || "Erreur lors de l'aperçu"
      };
    }
  }

  // ============================================================
  // ============== HELPERS DE FORMATAGE ========================
  // ============================================================

  static formatPrice(val) {
    if (val === null || val === undefined) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(val);
  }

  static formatDate(dateString) {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  static formatDateShort(dateString) {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  static daysUntil(dateString) {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `Retard ${Math.abs(diff)}j`;
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Demain';
    return `Dans ${diff}j`;
  }

  // ============================================================
  // ============== BOUTON FERMETURE ============================
  // ============================================================

  static getCloseButtonHTML() {
    return `
      <button id="ticket-close-btn" onclick="window.close()" aria-label="Fermer" title="Fermer">✕</button>
      <style>
        #ticket-close-btn {
          position: fixed;
          top: 12px;
          right: 12px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: #f0f0f0;
          color: black;
          font-size: 22px;
          font-weight: bold;
          cursor: pointer;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s ease, background 0.15s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          font-family: Arial, sans-serif;
          line-height: 1;
        }
        #ticket-close-btn:hover,
        #ticket-close-btn:active {
          transform: scale(1.1);
          background: #e0e0e0;
        }
        @media print {
          #ticket-close-btn {
            display: none !important;
          }
        }
      </style>
    `;
  }

  // ============================================================
  // ============== GÉNÉRATION HTML =============================
  // ============================================================

  /**
   * Génère le HTML complet du ticket de commande atelier.
   * - 80mm exact à l'impression
   * - Style alignement gauche/droite (label à gauche, valeur à droite)
   * - Pas de bordures horizontales
   */
  static generateOrderTicketHTML(order, boutique, creator, isPreview = false) {
    // ===== Données boutique =====
    const boutiqueName = boutique?.name || 'Ma Boutique';
    const boutiqueAddress = boutique?.address || '';
    const boutiquePhone = boutique?.phone || '';
    const boutiquePhone1 = boutique?.phone1 || '';
    const primaryColor = boutique?.primaryColor || '#3b82f6';

    // ===== Données client =====
    const customerName = order.customerName || 'Client';
    const customerPhone = order.customerPhone || '';
    const sellerName = creator?.name || order.sellerName || 'Vendeur';

    // ===== Données modèle =====
    const modelName = order.modelName 
      || order.customModelName 
      || 'Modèle personnalisé';
    const fabricDescription = order.fabricDescription || '';
    const fabricByCustomer = order.fabricProvidedByCustomer === true;

    // ===== Montants =====
    const subtotal = order.subtotal || order.totalPrice || 0;
    const discountAmount = order.discountAmount || 0;
    const total = order.totalPrice || 0;
    const advance = order.advanceAmount || 0;
    const remaining = order.remainingAmount !== undefined
      ? order.remainingAmount
      : (total - advance);

    const hasDiscount = discountAmount > 0;
    const discountLabel = order.discountType === 'PERCENTAGE'
      ? `Remise (${order.discountValue}%)`
      : 'Remise';

    // ===== Livraison =====
    const expectedDate = this.formatDate(order.expectedDeliveryDate);
    const expectedDays = this.daysUntil(order.expectedDeliveryDate);

    // ===== Statut & priorité =====
    const status = order.statusDisplayName || 'Reçue';
    const statusIcon = order.statusIcon || '🟡';
    const isPriorityUrgent = order.priority === 'URGENT';

    // ===== Notes =====
    const notes = order.notes || '';

    // ===== Logo HTML =====
    const logoHTML = generateBoutiqueLogoHTML(boutique, { width: 60, height: 50 });

    // ===== Contact boutique sur une ligne =====
    let boutiqueContacts = [];
    if (boutiquePhone) boutiqueContacts.push(`Tél: ${boutiquePhone}`);
    if (boutiquePhone1) boutiqueContacts.push(boutiquePhone1);
    const boutiqueContactsLine = boutiqueContacts.join(' • ');

    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=2" />
  <title>Bon de commande ${order.orderNumber}</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }

    * {
      box-sizing: border-box;
    }

    /* ===== AFFICHAGE ÉCRAN (DESKTOP) ===== */
    html {
      background: #f0f0f0;
      min-height: 100vh;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      width: 80mm;
      max-width: 80mm;
      margin: 20px auto;
      padding: 12px;
      font-size: 12px;
      line-height: 1.4;
      color: #000;
      background: white;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
      font-weight: 500;
      box-shadow: 0 4px 16px rgba(0,0,0,0.1);
      border-radius: 4px;
    }

    /* ===== HEADER (centré uniquement) ===== */
    .header {
      text-align: center;
      padding-bottom: 6px;
      margin-bottom: 8px;
    }

    .boutique-name {
      font-size: 16px;
      font-weight: 800;
      margin: 4px 0 2px;
      color: #000;
      letter-spacing: 0.3px;
      text-align: center;
    }

    .boutique-info {
      font-size: 11px;
      line-height: 1.3;
      color: #000;
      font-weight: 500;
      text-align: center;
    }

    /* ===== TITRE TICKET (centré) ===== */
    .ticket-header {
      text-align: center;
      margin: 8px 0;
      padding: 6px;
      background: #f5f5f5;
      border-radius: 4px;
    }

    .ticket-title {
      font-weight: 800;
      font-size: 11px;
      letter-spacing: 0.5px;
      color: #000;
      margin-bottom: 2px;
      text-align: center;
    }

    .order-number {
      font-weight: 800;
      font-size: 13px;
    //   color: ${primaryColor};
      color: #000;
      letter-spacing: 0.5px;
      text-align: center;
    }

    /* ===== LIGNES INFOS GAUCHE/DROITE ===== */
    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 11px;
      margin-bottom: 4px;
      color: #000;
      font-weight: 500;
    }

    .info-row .label {
      color: #000;
      font-weight: 600;
    }

    .info-row .value {
      color: #000;
      font-weight: 700;
      text-align: right;
    }

    /* ===== SECTIONS ===== */
    .section {
      margin: 10px 0;
    }

    

    /* ===== LIGNES DE DONNÉES GAUCHE/DROITE ===== */
    .data-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 12px;
      padding: 2px 0;
      gap: 8px;
    }

    .data-row .label {
      color: #000;
      font-weight: 500;
      flex-shrink: 0;
    }

    .data-row .value {
      font-weight: 700;
      color: #000;
      text-align: right;
      word-break: break-word;
    }

    /* ===== TOTAL (centré) ===== */
    .total-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding: 6px 4px;
      font-weight: 800;
      font-size: 14px;
      margin-top: 6px;
      color: white;
      background: #000;
      border-radius: 3px;
    }

    .total-row .label {
      color: white;
    }

    .total-row .value {
      color: white;
    }

    .discount {
      color: #000 !important;
      font-weight: 700;
    }

    .discount .label,
    .discount .value {
      color: #000 !important;
    }

    /* ===== AVANCE / SOLDE GAUCHE/DROITE ===== */
    .balance-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding: 4px 0;
      font-size: 12px;
    }

    .balance-row.solde {
      font-size: 13px;
      font-weight: 800;
      margin-top: 2px;
      padding-top: 4px;
    }

    .balance-row .label {
      color: #000;
      font-weight: 500;
    }

    .balance-row.solde .label {
      color: #000;
      font-weight: 800;
    }

    .balance-row .value {
      font-weight: 700;
    }

    /* ===== BADGE URGENT ===== */
    .urgent-badge {
      display: inline-block;
      background: #dc2626;
      color: white;
      padding: 1px 6px;
      border-radius: 8px;
      font-size: 9px;
      font-weight: 800;
      margin-left: 4px;
      letter-spacing: 0.5px;
    }

    /* ===== BLOC LIVRAISON ===== */
    .delivery-box {
      border: 2px solid ${primaryColor};
      border-radius: 5px;
      padding: 6px 8px;
      margin: 10px 0;
      text-align: center;
      background: #fafafa;
    }

    .delivery-line {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 12px;
      font-weight: 700;
      color: #000;
    }

    .delivery-label {
      font-weight: 700;
      color: #000;
    }

    .delivery-value {
      text-align: right;
    }

    .delivery-date {
      font-weight: 800;
      font-size: 13px;
      //color: ${primaryColor};
      color: #000
    }

    .delivery-days {
      font-size: 10px;
      color: #000;
      font-weight: 600;
      margin-left: 4px;
    }

    /* ===== SIGNATURES ===== */
    .signatures {
      display: flex;
      justify-content: space-between;
      margin-top: 12px;
      gap: 12px;
    }

    .signature-box {
      flex: 1;
      text-align: center;
      font-size: 10px;
      color: #333;
      font-weight: 600;
    }

    .signature-line {
      border-top: 1px solid #000;
      margin-top: 22px;
      padding-top: 3px;
      font-weight: 600;
      text-align: center;
    }

    /* ===== FOOTER (centré) ===== */
    .footer {
      text-align: center;
      margin-top: 12px;
      padding-top: 6px;
      font-size: 11px;
    }

    .footer strong {
      font-size: 12px;
      color: #000;
      font-weight: 800;
    }

    .footer-info {
      margin-top: 2px;
      font-size: 10px;
      color: #000;
      line-height: 1.3;
      font-weight: 500;
    }

    /* ===== NOTES ===== */
    .notes-box {
      background: #fffbeb;
      padding: 4px 8px;
      margin: 4px 0 0;
      font-style: italic;
      font-size: 11px;
      color: #000;
      font-weight: 500;
      text-align: left;
      border-radius: 2px;
    }

    /* ===== CLIENT (centré) ===== */
    .customer-line {
      font-size: 12px;
      font-weight: 800;
      color: #000;
      text-align: center;
    }

    .customer-line .name {
      font-weight: 800;
    }

    .customer-line .phone {
      font-weight: 600;
      color: #000;
    }

    /* ===== TISSU (gauche/droite) ===== */
    .fabric-source-inline {
      font-size: 10px;
      color: #000;
      font-style: italic;
      margin-left: 4px;
    }

    /* ===== ADAPTATION MOBILE (< 600px) ===== */
    @media screen and (max-width: 600px) {
      html {
        background: white;
      }
      body {
        width: 95vw;
        max-width: 95vw;
        margin: 10px auto;
        padding: 16px 12px;
        font-size: 15px;
        line-height: 1.4;
        box-shadow: none;
        border-radius: 0;
      }

      .boutique-name { font-size: 20px !important; }
      .boutique-info { font-size: 14px !important; }
      .ticket-title { font-size: 14px !important; }
      .order-number { font-size: 16px !important; }
      .info-row { font-size: 14px !important; }
      .data-row { font-size: 14px !important; }
      .total-row { font-size: 17px !important; }
      .customer-line { font-size: 14px !important; }
      .balance-row { font-size: 14px !important; }
      .balance-row.solde { font-size: 16px !important; }
      .delivery-line { font-size: 14px !important; }
      .delivery-date { font-size: 16px !important; }
      .delivery-days { font-size: 12px !important; }
      .signature-box { font-size: 12px !important; }
      .footer strong { font-size: 14px !important; }
      .footer-info { font-size: 12px !important; }
      .notes-box { font-size: 13px !important; }
      .urgent-badge { font-size: 11px !important; padding: 2px 8px; }
      .fabric-source-inline { font-size: 12px !important; }
    }

    /* ===== IMPRESSION (forcer 80mm exact) ===== */
    @media print {
      html {
        background: white !important;
      }
      body {
        width: 80mm !important;
        max-width: 80mm !important;
        margin: 0 !important;
        padding: 5px !important;
        font-size: 12px !important;
        line-height: 1.4 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .boutique-name { font-size: 16px !important; }
      .boutique-info { font-size: 11px !important; }
      .ticket-title { font-size: 11px !important; }
      .order-number { font-size: 13px !important; }
      .info-row { font-size: 11px !important; }
      .section-title { font-size: 11px !important; }
      .data-row { font-size: 12px !important; }
      .total-row { font-size: 14px !important; }
      .customer-line { font-size: 12px !important; }
      .balance-row { font-size: 12px !important; }
      .balance-row.solde { font-size: 13px !important; }
      .delivery-line { font-size: 12px !important; }
      .delivery-date { font-size: 13px !important; }
      .delivery-days { font-size: 10px !important; }
      .signature-box { font-size: 10px !important; }
      .footer strong { font-size: 12px !important; }
      .footer-info { font-size: 10px !important; }
      .notes-box { font-size: 11px !important; }
      .urgent-badge { font-size: 9px !important; padding: 1px 6px; }
      .fabric-source-inline { font-size: 10px !important; }

      .no-print {
        display: none !important;
      }
    }
  </style>
</head>
<body>

  ${isPreview ? this.getCloseButtonHTML() : ''}

  <!-- Logo + Boutique (centré) -->
  <div class="header">
    ${logoHTML}
    <div class="boutique-name">${boutiqueName.toUpperCase()}</div>
    ${boutiqueAddress ? `<div class="boutique-info">${boutiqueAddress}</div>` : ''}
    ${boutiqueContactsLine ? `<div class="boutique-info">${boutiqueContactsLine}</div>` : ''}
  </div>

  <!-- Titre commande (centré) -->
  <div class="ticket-header">
    <div class="ticket-title">BON DE COMMANDE ATELIER</div>
    <div class="order-number">N° ${order.orderNumber}</div>
  </div>

  <!-- ✅ Date / Vendeur GAUCHE-DROITE -->
  <div class="info-row">
    <span class="label">Date :</span>
    <span class="value">${this.formatDateShort(order.createdAt || order.orderDate)}</span>
  </div>
  <div class="info-row">
    <span class="label">Vendeur :</span>
    <span class="value">${sellerName}</span>
  </div>
  <div class="info-row">
    <span class="label">Statut :</span>
    <span class="value"> ${status}${isPriorityUrgent ? 'URGENT</span>' : ''}</span>
  </div>

  <!-- Client (centré) -->

  <div class="section">
    <div class="section-title">Client</div>
    <div class="data-row">
      <span class="label">Nom :</span>
      <span class="name">${customerName}</span>
    </div>
    ${customerPhone ? `
      <div class="data-row">
        <span class="label">Téléphone :</span>
        <span class="phone">${customerPhone}<span
      </div>
    ` : ''}
  </div>

  

  <!-- ✅ Détails commande GAUCHE-DROITE -->
  <div class="section">
    <div class="section-title">Détails commande</div>
    <div class="data-row">
      <span class="label">Modèle :</span>
      <span class="value">${modelName}</span>
    </div>
    ${fabricDescription ? `
      <div class="data-row">
        <span class="label">Tissu :</span>
        <span class="value">${fabricDescription}<span class="fabric-source-inline">${fabricByCustomer ? '(client)' : "(atelier)"}</span></span>
      </div>
    ` : ''}
  </div>

  <!-- ✅ Montants GAUCHE-DROITE -->
  <div class="section">
    <div class="section-title">Montants</div>
    ${hasDiscount ? `
      <div class="data-row">
        <span class="label">Sous-total :</span>
        <span class="value">${this.formatPrice(subtotal)}</span>
      </div>
      <div class="data-row discount">
        <span class="label">${discountLabel} :</span>
        <span class="value">-${this.formatPrice(discountAmount)}</span>
      </div>
    ` : ''}
    
    <div class="total-row">
      <span class="label">TOTAL</span>
      <span class="value">${this.formatPrice(total)}</span>
    </div>

    <div class="balance-row" style="margin-top:6px;">
      <span class="label">Avance versée :</span>
      <span class="value" style="color:#000;">${this.formatPrice(advance)}</span>
    </div>
    <div class="balance-row solde">
      <span class="label">SOLDE :</span>
      <span class="value" style="color:${remaining > 0 ? '#000' : '#000'};">${this.formatPrice(remaining)}</span>
    </div>
  </div>

  <!-- Livraison (gauche/droite) -->
  <div class="delivery-box">
    <div class="delivery-line">
      <span class="delivery-label">📅 Livraison prevue:</span>
      <span class="delivery-value">
        <span class="delivery-date">${expectedDate}</span>
        ${expectedDays ? `<span class="delivery-days">(${expectedDays})</span>` : ''}
      </span>
    </div>
  </div>

  <!-- Notes -->
  ${notes ? `
  <div class="section">
    <div class="section-title">Notes</div>
    <div class="notes-box">${notes}</div>
  </div>
  ` : ''}

  <!-- Footer (centré) -->
  <div class="footer">
    <strong>Merci de votre confiance !</strong>
    <div class="footer-info">Conservez ce bon pour le retrait de votre tenue</div>
  </div>

</body>
</html>
    `;
  }
}

export default OrderTicketPrintService;